import { useState, useMemo, useCallback } from 'react';
import Navbar from '../components/Navbar';
import { trackToolAction } from '../utils/metrics';
import { stripVin, decodeLocal } from '../utils/vinDecode';
import { decodeVinNhtsa, RESULT_FIELDS } from '../utils/nhtsaVin';

const smallButton = {
  background: 'none', border: '1px solid #e2e8f0', borderRadius: 6,
  padding: '5px 12px', cursor: 'pointer', fontSize: 12, color: '#475569', fontWeight: 600,
};

function Row({ label, children }) {
  return (
    <div style={{ display: 'flex', gap: 14, padding: '9px 0', borderTop: '1px solid #f1f5f9' }}>
      <div style={{ width: 150, flexShrink: 0, fontSize: 12, fontWeight: 700, color: '#64748b' }}>
        {label}
      </div>
      <div style={{ fontSize: 14, color: '#0f172a', lineHeight: 1.5 }}>{children}</div>
    </div>
  );
}

export default function VinDecoder() {
  const [input, setInput] = useState('');
  const [nhtsa, setNhtsa] = useState(null);      // decoded NHTSA row
  const [nhtsaLoading, setNhtsaLoading] = useState(false);
  const [nhtsaError, setNhtsaError] = useState('');

  const vin = stripVin(input);
  const local = useMemo(() => (vin ? decodeLocal(vin) : null), [vin]);

  const onChange = (e) => {
    setInput(e.target.value);
    setNhtsa(null);
    setNhtsaError('');
  };

  const runFullDecode = useCallback(async () => {
    if (!local?.validFormat) return;
    setNhtsaLoading(true);
    setNhtsaError('');
    trackToolAction('/vindecoder', 'VIN Decoder');
    try {
      const row = await decodeVinNhtsa(vin);
      setNhtsa(row);
    } catch (err) {
      setNhtsaError(err?.message || String(err));
    } finally {
      setNhtsaLoading(false);
    }
  }, [vin, local]);

  const remaining = 17 - vin.length;

  return (
    <div style={{
      minHeight: '100vh',
      background: '#f8fafc',
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      color: '#0f172a',
    }}>
      <Navbar pageTitle="VIN Decoder" />

      <div style={{ maxWidth: 720, margin: '0 auto', padding: '32px 24px 72px' }}>

        {/* Privacy explainer */}
        <div style={{
          background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 12,
          padding: '14px 18px', marginBottom: 24, display: 'flex', gap: 12, alignItems: 'flex-start',
        }}>
          <div style={{ fontSize: 20, lineHeight: 1 }}>🔒</div>
          <div style={{ fontSize: 13, color: '#065f46', lineHeight: 1.6 }}>
            <strong>Checksum validation, country, and model year are computed locally</strong> — no
            network request, ever, for those. The only thing that leaves your browser is an
            optional, explicit <strong>Full decode</strong> lookup, which sends the VIN (nothing
            else) to NHTSA&rsquo;s free public vehicle database and only runs when you press it.
          </div>
        </div>

        {/* Input */}
        <div style={{
          background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 12,
          padding: '18px 20px', marginBottom: 20,
        }}>
          <label htmlFor="vin-input" style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 6 }}>
            VIN
          </label>
          <input
            id="vin-input"
            value={input}
            onChange={onChange}
            placeholder="17-character VIN"
            maxLength={20}
            style={{
              width: '100%', fontSize: 18, fontFamily: 'monospace', letterSpacing: '1px',
              padding: '10px 12px', border: '1.5px solid #cbd5e1', borderRadius: 8,
              textTransform: 'uppercase', boxSizing: 'border-box',
            }}
          />
          <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 6 }}>
            {vin.length === 0 && 'Enter a 17-character VIN.'}
            {vin.length > 0 && vin.length < 17 && `${remaining} more character${remaining === 1 ? '' : 's'} needed.`}
            {vin.length === 17 && !local?.validFormat && 'Contains a character not used in VINs (I, O, and Q are never valid).'}
            {vin.length === 17 && local?.validFormat && '17 characters — valid format.'}
            {vin.length > 17 && `${vin.length - 17} character${vin.length - 17 === 1 ? '' : 's'} too many.`}
          </div>
        </div>

        {/* Local decode */}
        {local?.validFormat && (
          <div style={{
            background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 12,
            padding: '18px 20px', marginBottom: 20,
          }}>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>Decoded locally</div>

            <Row label="Check digit">
              {local.checkDigitValid === true && (
                <span style={{ color: '#15803d', fontWeight: 700 }}>✓ Valid — internally consistent</span>
              )}
              {local.checkDigitValid === false && (
                <span style={{ color: '#b91c1c', fontWeight: 700 }}>
                  ✗ Invalid — this VIN fails its own checksum (transcription error, or not genuine)
                </span>
              )}
            </Row>

            <Row label="Country of origin">
              {local.country || <span style={{ color: '#94a3b8' }}>Not recognized</span>}
            </Row>

            <Row label="Likely manufacturer">
              {local.manufacturer
                ? <>{local.manufacturer} <span style={{ color: '#94a3b8', fontSize: 12 }}>(from a common-manufacturer reference list — not exhaustive; use Full decode to confirm)</span></>
                : <span style={{ color: '#94a3b8' }}>Not in this tool&rsquo;s reference list — try Full decode below</span>}
            </Row>

            <Row label="Model year">
              {local.modelYears.length ? (
                <>
                  <strong>{local.modelYears[0].year}</strong>
                  {local.modelYears[1] && (
                    <span style={{ color: '#94a3b8', fontSize: 12.5 }}>
                      {' '}(or {local.modelYears[1].year} — this position repeats every 30 years;{' '}
                      {local.modelYears[0].year} is favored based on position 7 of the VIN, a common
                      convention, not a guarantee)
                    </span>
                  )}
                </>
              ) : <span style={{ color: '#94a3b8' }}>Not decodable</span>}
            </Row>
          </div>
        )}

        {/* Full decode */}
        {local?.validFormat && (
          <div style={{
            background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 12,
            padding: '18px 20px', marginBottom: 20,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 200 }}>
                <div style={{ fontSize: 13, fontWeight: 700 }}>Full decode</div>
                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>
                  Exact make, model, trim, engine, and plant — sends this VIN to{' '}
                  <strong>vpic.nhtsa.dot.gov</strong>, NHTSA&rsquo;s free public API. Nothing else
                  about you or this session is sent.
                </div>
              </div>
              <button
                onClick={runFullDecode}
                disabled={nhtsaLoading}
                style={{
                  background: nhtsaLoading ? '#c7d2fe' : '#4f46e5', color: '#fff', border: 'none',
                  borderRadius: 8, padding: '10px 20px', fontSize: 13.5, fontWeight: 700,
                  cursor: nhtsaLoading ? 'default' : 'pointer', flexShrink: 0,
                }}
              >
                {nhtsaLoading ? 'Looking up…' : 'Full decode'}
              </button>
            </div>

            {nhtsaError && (
              <div style={{ marginTop: 12, fontSize: 12.5, color: '#b91c1c', lineHeight: 1.6 }}>
                {nhtsaError}
              </div>
            )}

            {nhtsa && (
              <div style={{ marginTop: 12 }}>
                {RESULT_FIELDS.filter(f => nhtsa[f.key]).map(f => (
                  <Row key={f.key} label={f.label}>{nhtsa[f.key]}</Row>
                ))}
                {!RESULT_FIELDS.some(f => nhtsa[f.key]) && (
                  <div style={{ fontSize: 13, color: '#94a3b8' }}>NHTSA returned no details for this VIN.</div>
                )}
              </div>
            )}
          </div>
        )}

        {vin.length === 17 && !local?.validFormat && (
          <div style={{
            background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 10,
            padding: '12px 16px', fontSize: 13, color: '#b91c1c', lineHeight: 1.6,
          }}>
            This isn&rsquo;t a valid VIN format — the letters I, O, and Q are never used (to avoid
            confusion with 1 and 0). Double-check the transcription.
          </div>
        )}

        {!vin && (
          <div style={{ textAlign: 'center', color: '#94a3b8', fontSize: 14, marginTop: 40, lineHeight: 1.7 }}>
            Enter a VIN to validate its check digit and decode its country, likely manufacturer,
            and model year — entirely offline.
          </div>
        )}

        {vin && (
          <div style={{ marginTop: 20, textAlign: 'right' }}>
            <button onClick={() => setInput('')} style={smallButton}>Clear</button>
          </div>
        )}
      </div>
    </div>
  );
}
